import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClinicServices, deleteClinicService } from '../../store/thunks/clinicThunk';
import AddServiceToClinicModal from './AddServiceToClinicModal';
import { logInfo, logError } from '../../utils/logger';

export default function ClinicServices({ clinic, onUpdateClinic, editMode = false }) {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [saveStatus, setSaveStatus] = useState('initial');
    const { services, status, error } = useSelector((state) => state.clinics.selectedClinic || {});

    useEffect(() => {
        if (clinic?._id) {
            dispatch(fetchClinicServices(clinic._id))
                .unwrap()
                .then(() => {
                    logInfo('Clinic services fetched successfully', { clinicId: clinic._id });
                })
                .catch((error) => {
                    logError('Failed to fetch clinic services', { error, clinicId: clinic._id });
                });
        }
    }, [dispatch, clinic]);

    const handleDeleteService = async (serviceId) => {
        if (window.confirm('Are you sure you want to delete this service?')) {
            setSaveStatus('saving');
            try {
                await dispatch(deleteClinicService({ clinicId: clinic._id, serviceId })).unwrap();
                logInfo('Service deleted successfully', { clinicId: clinic._id, serviceId });
                setSaveStatus('saved');
                onUpdateClinic();
                setTimeout(() => setSaveStatus('initial'), 2000);
            } catch (error) {
                logError('Failed to delete service', { error, clinicId: clinic._id, serviceId });
                setSaveStatus('error');
            }
        }
    };

    const handleEditService = (service) => {
        setEditingService(service);
        setIsModalOpen(true);
    };

    const handleAddService = () => {
        setEditingService(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingService(null);
    };

    const formatPrice = (price) => {
        return price ? `$${parseFloat(price).toFixed(2)}` : 'Not set';
    };

    const formatDuration = (duration) => {
        return duration ? `${duration} minutes` : 'Not set';
    };

    if (status === 'loading') {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="text-sm text-gray-600">Loading services...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="text-sm text-red-600">Error: {error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                <div>
                    <h2 className="text-lg font-medium text-gray-900">Clinic Services</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage services offered at this clinic</p>
                </div>
                {editMode && (
                    <button
                        onClick={handleAddService}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-sm text-white bg-gray-900 hover:bg-gray-800 transition-colors"
                    >
                        Add Service
                    </button>
                )}
            </div>

            {/* Services List */}
            <div className="space-y-4">
                {services && services.length > 0 ? (
                    <div className="grid gap-4">
                        {services.map((service) => (
                            <div key={service._id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-sm">
                                <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h3 className="text-sm font-medium text-gray-900">
                                                {service.name}
                                            </h3>
                                            {service.description && (
                                                <p className="text-sm text-gray-600 mt-1">
                                                    {service.description}
                                                </p>
                                            )}
                                            <div className="flex items-center space-x-4 mt-2">
                                                <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800">
                                                    {formatDuration(service.duration)}
                                                </span>
                                                <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-green-100 text-green-800">
                                                    {formatPrice(service.price)}
                                                </span>
                                            </div>
                                        </div>
                                        {editMode && (
                                            <div className="flex items-center space-x-2 ml-4">
                                                <button
                                                    onClick={() => handleEditService(service)}
                                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteService(service._id)}
                                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <div className="text-sm">No services configured for this clinic.</div>
                        {editMode && (
                            <button
                                onClick={handleAddService}
                                className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-sm text-white bg-gray-900 hover:bg-gray-800 transition-colors"
                            >
                                Add First Service
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Status Messages */}
            {saveStatus === 'saved' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                    <div className="text-sm text-green-800">Services updated successfully!</div>
                </div>
            )}

            {saveStatus === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                    <div className="text-sm text-red-800">Failed to update services. Please try again.</div>
                </div>
            )}

            <AddServiceToClinicModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                clinicId={clinic._id}
                editingService={editingService}
            />
        </div>
    );
}
